import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, X, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// 🆕 Feb 2026 — Item singolo trascinabile
function SortableItem({ id, label, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded ${isDragging ? "shadow-md" : ""}`}
      data-testid={`signature-doc-type-item-${id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-0.5"
        title="Trascina per riordinare"
        data-testid={`signature-doc-type-drag-${id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-sm text-slate-800 font-medium truncate flex-1" title={label}>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-rose-600 hover:bg-rose-50 rounded p-1"
        title="Elimina"
        data-testid={`signature-doc-type-remove-${id}`}
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}

/**
 * 🆕 Feb 2026 — Editor riutilizzabile per la lista `signature_doc_types` dell'agenzia.
 * Backend: GET/PUT /api/agency-settings/signature-doc-types.
 *
 * Props:
 *   - onSaved: (types: string[]) => void — callback chiamato dopo un salvataggio riuscito
 *   - showBadge: bool — mostra badge "preset di default" quando la lista non è personalizzata
 */
export default function SignatureDocTypesEditor({ onSaved, showBadge = true }) {
  const [types, setTypes] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState("");
  const [initial, setInitial] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency-settings/signature-doc-types");
      const arr = Array.isArray(data?.types) ? data.types : [];
      setTypes(arr);
      setInitial(arr);
      setIsDefault(!!data?.is_default);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addType = () => {
    const t = (newType || "").trim();
    if (!t) return;
    if (types.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.error("Titolo già presente");
      return;
    }
    if (t.length > 100) {
      toast.error("Titolo troppo lungo (max 100 caratteri)");
      return;
    }
    if (types.length >= 30) {
      toast.error("Massimo 30 tipi documento");
      return;
    }
    setTypes([...types, t]);
    setNewType("");
  };

  const removeType = (idx) => setTypes(types.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/agency-settings/signature-doc-types", { types });
      const arr = Array.isArray(data?.types) ? data.types : [];
      setTypes(arr);
      setInitial(arr);
      setIsDefault(!!data?.is_default);
      toast.success("Tipi documento salvati");
      onSaved?.(arr);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetLocal = () => setTypes(initial);
  const dirty = JSON.stringify(types) !== JSON.stringify(initial);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = types.indexOf(active.id);
    const newIndex = types.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setTypes(arrayMove(types, oldIndex, newIndex));
  };

  if (loading) {
    return <div className="py-6 text-center text-sm text-slate-400">Caricamento…</div>;
  }

  return (
    <div className="space-y-4" data-testid="signature-doc-types-editor">
      <p className="text-sm text-slate-600">
        Aggiungi o rimuovi i titoli disponibili nella tendina di firma.
        {showBadge && isDefault && (
          <span className="ml-1 inline-block px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800">
            preset di default (non ancora personalizzati)
          </span>
        )}
      </p>

      {types.length === 0 ? (
        <div className="py-4 px-3 text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded">
          Nessun tipo documento configurato. Aggiungine almeno uno dal form qui sotto.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={types} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="signature-doc-types-list">
              {types.map((t) => (
                <SortableItem
                  key={t}
                  id={t}
                  label={t}
                  onRemove={() => setTypes(types.filter((x) => x !== t))}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="new-sig-doc-type" className="text-xs">Nuovo titolo</Label>
          <Input
            id="new-sig-doc-type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addType(); } }}
            placeholder="Es. Contratto Locazione"
            maxLength={100}
            data-testid="signature-doc-type-new-input"
          />
        </div>
        <Button
          type="button"
          onClick={addType}
          disabled={!newType.trim() || types.length >= 30}
          className="bg-slate-800 hover:bg-slate-900 text-white"
          data-testid="signature-doc-type-add-btn"
        >
          Aggiungi
        </Button>
      </div>
      <p className="text-[11px] text-slate-400">
        {types.length}/30 tipi · Le modifiche non sono salvate finché non premi &quot;Salva&quot;.
      </p>

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="signature-doc-types-save"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </Button>
        {dirty && (
          <Button
            type="button"
            variant="outline"
            onClick={resetLocal}
            disabled={saving}
            data-testid="signature-doc-types-reset"
          >
            Annulla modifiche
          </Button>
        )}
      </div>
    </div>
  );
}
